import { useParams } from 'react-router'

import { Breadcrumb, Group, Post, Thread } from 'lib/_types'
import { ROUTE_PATH } from 'lib/paths'
import { last } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useGetGroupQuery } from 'store/api/yay/group'
import { useGetPostQuery } from 'store/api/yay/post'
import { useGetThreadQuery } from 'store/api/yay/thread'
import { useBreadcrumb } from './useBreadcrumb'
import { useEffect } from 'react'

type BreadcrumbCreator = (props: {
  group?: Group
  thread?: Thread
  post?: Post
}) => Breadcrumb[]

export const useGroupOrThreadOrPostPageData = (
  breadcrumb?: Breadcrumb[] | BreadcrumbCreator
) => {
  const { t } = useTranslation()
  const { groupId, threadId, postId } = useParams()
  const { setBreadcrumb } = useBreadcrumb()

  const isValidGroupId = isValidId(groupId)
  const isValidThreadId = isValidId(threadId)
  const isValidPostId = isValidId(postId)

  const groupRes = useGetGroupQuery({ groupId }, { skip: !isValidGroupId })
  const threadRes = useGetThreadQuery({ threadId }, { skip: !isValidThreadId })
  const postRes = useGetPostQuery({ postId }, { skip: !isValidPostId })

  const isGroupDataReady = !groupId || groupRes.isSuccess
  const isThreadDataReady = !threadId || threadRes.isSuccess
  const isPostDataReady = !postId || postRes.isSuccess
  const isDataReady = isGroupDataReady && isThreadDataReady && isPostDataReady

  // breadcrumb
  useEffect(() => {
    if (breadcrumb && isDataReady) {
      const prefixBreadcrumb: Breadcrumb[] = []

      if (groupId) {
        prefixBreadcrumb.push(
          {
            title: t('common.group'),
            link: ROUTE_PATH.GROUPS,
          },
          {
            title: groupRes?.data?.topic ?? postRes?.data?.group?.topic,
            link: ROUTE_PATH.GROUP(groupId),
          }
        )

        if (threadId) {
          prefixBreadcrumb.push(
            {
              title: t('common.thread'),
              link: ROUTE_PATH.GROUP_THREADS(groupId),
            },
            {
              title: threadRes?.data?.title,
              link: ROUTE_PATH.GROUP_THREAD(groupId, threadId),
            }
          )
        }
      } else {
        prefixBreadcrumb.push({
          title: t('common.home'),
          link: ROUTE_PATH.HOME,
        })
      }

      const newBreadcrumb = [
        ...prefixBreadcrumb,
        ...(typeof breadcrumb === 'function'
          ? breadcrumb({
              group: groupRes?.data,
              thread: threadRes?.data,
              post: postRes?.data?.post,
            })
          : breadcrumb),
      ]
      delete last(newBreadcrumb)?.link

      setBreadcrumb(newBreadcrumb)
    }
  }, [isDataReady])

  return {
    groupId,
    isValidGroupId,
    groupRes,
    isGroupDataReady,

    threadId,
    isValidThreadId,
    threadRes,
    isThreadDataReady,

    postId,
    isValidPostId,
    postRes,
    isPostDataReady,

    isDataReady,
  }
}

const isValidId = (id) => /^\d+$/.test(id?.toString())
